//region header */
//--------------------------------------------------------------------------------------------------
//
//  Description: Street Page
//
//  Copyright:    © 2021 - 2026 Idox Software Limited.
//
//--------------------------------------------------------------------------------------------------
//
//  Modification History:
//
//  Version Date     Modifier             Issue# Description
//region Version 1.0.0.0
//    001   20.07.21 Sean Flook          WI39??? Initial Revision.
//    002   23.08.23 Sean Flook        IMANN-159 Use the street template defaults whe getting a new street.
//    003   07.09.23 Sean Flook                  Cleaned the code.
//    004   06.10.23 Sean Flook                  Added lookupContext so it can be passed through to GetNewStreet.
//    005   02.01.24 Sean Flook                  Changed console.log to console.error for error messages.
//    006   25.01.24 Sean Flook                  Changes required after UX review.
//    007   26.01.24 Sean Flook        IMANN-232 Correctly initialise loadingRef.
//    008   14.02.24 Sean Flook                  Added a bit of error trapping.
//    009   05.04.24 Sean Flook                  Correctly handle errors when getting a street.
//    010   05.04.24 Sean Flook        IMANN-351 Changes to handle browser navigation.
//    011   11.04.24 Sean Flook        IMANN-351 Prevent infinite loops when creating a new record.
//    012   18.04.24 Sean Flook        IMANN-351 Changes required to reload the contexts after a refresh.
//    017   08.05.24 Sean Flook        IMANN-447 Added exclude from export from template.
//    018   19.06.24 Sean Flook        IMANN-629 Changes to code so that current user is remembered and a 401 error displays the login dialog.
//    019   20.06.24 Sean Flook        IMANN-636 Use the new user rights.
//    020   21.06.24 Sean Flook        IMANN-636 Pass through hasASD to GetNewStreet.
//    021   26.07.24 Sean Flook        IMANN-850 Store the apiUrl so that on refresh we are still using the correct URL.
//    022   05.09.24 Sean Flook        IMANN-575 Added additional debug message.
//    023   10.09.24 Sean Flook        IMANN-980 Only write to the console if the user has the showMessages right.
//endregion Version 1.0.0.0
//region Version 1.0.5.0
//    024   27.01.25 Sean Flook       IMANN-1077 Upgraded MUI to v6.
//    025   30.01.25 Sean Flook       IMANN-1673 Changes required for new user settings API.
//    026   23.04.25 Joshua McCormick IMANN-1743 Commented out all sessionStorage calls
//    027   03.04.25 Joshua McCormick IMANN-1743 Added GetStreetByUSRNUrl and apiUrl
//    028   03.04.25 Joshua McCormick IMANN-1743 GetStreetByUSRNUrl checks if !apiUrl
//    029   24.04.25 Sean Flook       IMANN-1760 Reenabled above change as required.
//endregion Version 1.0.5.0
//region Version 1.0.6.4
//    030   01.10.25 Sean Flook       IMANN-2077 Use -1 rather than 0 for new street USRN.
//endregion Version 1.0.6.4
//region Version 1.0.7.0
//    031   13.06.25 Sean Flook       IMANN-1812 Modified to use the new Sandbox store.
//    032   28.08.25 Sean Flook       IMANN-1812 Changes required to update MUI to v7.
//    033   27.10.25 Sean Flook       IMANN-2092 Use the new spatialReferenceWkid property from mapContext to set the spatialReference used by the layers in the map.
//    034   27.10.25 Sean Flook       IMANN-2111 Added react-resizable-panels.
//    035   28.10.25 Sean Flook       IMANN-2111 Change colour of resize handles when hovering cursor over them.
//    036   06.11.25 Sean Flook       IMANN-2092 Correctly set the spatialReferenceWkid variable.
//    037   17.12.25 Sean Flook       IMANN-1705 Set mapContext.useSearchExtentChange to true when loading a street so that the map uses the street extent when loading.
//    038   27.01.26 Sean Flook       IMANN-2238 Added code to restore a street after creating a property from the street.
//    039   24.03.26 Sean Flook       IMANN-2369 Only use the startExtent if required.
//    040   30.03.26 Sean Flook       IMANN-2377 If creating a new street, use the current map extent as the startExtent.
//    041   07.04.26 Sean Flook       IMANN-2395 Added timeouts to ensure data is loaded correctly.
//endregion Version 1.0.7.0
//region Version 1.0.7.1
//    042   28.04.26 Sean Flook       IMANN-2415 Removed useSearchExtent as not required.
//endregion Version 1.0.7.1
//
//--------------------------------------------------------------------------------------------------
//endregion header */

//region imports

import React, { useContext, useState, useRef, useEffect } from "react";
import { useLocation } from "react-router";
import useSandboxStore from "../context/SandboxStore";

import StreetContext from "../context/streetContext";
import UserContext from "../context/userContext";
import MapContext from "../context/mapContext";
import SettingsContext from "../context/settingsContext";
import LookupContext from "../context/lookupContext";
import PropertyContext from "../context/propertyContext";
import SearchContext from "../context/searchContext";
import InformationContext from "../context/informationContext";

import { GetStreetByUSRNUrl } from "../configuration/ADSConfig";
import { UpdateSandbox } from "../utils/HelperUtils";
import { GetNewStreet, GetMultipleEsusData, getStreetExtent } from "../utils/StreetUtils";
import { StreetRoute } from "../PageRouting";

import { Box } from "@mui/material";
import { Panel, PanelGroup, PanelResizeHandle } from "react-resizable-panels";

import { EditConfirmationServiceProvider } from "./EditConfirmationPage";
import StreetDataForm from "../forms/StreetDataForm";
import ADSMap from "../components/ADSMap";

import { dataFormStyle } from "../utils/ADSStyles";
import { adsLightGreyD } from "../utils/ADSColours";

//endregion imports

function StreetPage() {
  const sandboxStore = useSandboxStore((state) => state);
  const setStore = useSandboxStore((state) => state.setStore);

  const streetContext = useContext(StreetContext);
  const userContext = useContext(UserContext);
  const mapContext = useContext(MapContext);
  const settingsContext = useContext(SettingsContext);
  const lookupContext = useContext(LookupContext);
  const propertyContext = useContext(PropertyContext);
  const searchContext = useContext(SearchContext);
  const informationContext = useContext(InformationContext);

  const location = useLocation();

  const [apiUrl, setApiUrl] = useState(null);
  const [data, setData] = useState();
  const dataUsrn = useRef(-1);
  const [loading, setLoading] = useState(true);
  const loadingRef = useRef(false);
  const [reloadContexts, setReloadContexts] = useState(false);
  const [streetExtent, setStreetExtent] = useState(null);

  const spatialReferenceWkid = useRef(mapContext.spatialReference || 27700);

  useEffect(() => {
    if (sessionStorage.getItem("StreetPage_firstLoadDone") === null) {
      sessionStorage.setItem("StreetPage_firstLoadDone", 1);
    } else {
      setReloadContexts(true);
    }
  }, []);

  useEffect(() => {
    if (reloadContexts) {
      setReloadContexts(false);

      // Reload user context first
      userContext.onReload();

      // Then reload settings context (depends on user context)
      setTimeout(() => {
        settingsContext.onReload();

        // Then reload search contexts (depends on user and settings contexts)
        setTimeout(() => {
          searchContext.onReload();

          // Finally reload all other contexts (depend on user, settings and search contexts)
          setTimeout(() => {
            lookupContext.onReload();
            informationContext.onReload();
            propertyContext.onReload();
            streetContext.onReload();
            mapContext.onReload();
          }, 0);
        }, 0);
      }, 0);
    }
  }, [
    reloadContexts,
    userContext,
    propertyContext,
    mapContext,
    settingsContext,
    streetContext,
    lookupContext,
    searchContext,
    informationContext,
  ]);

  useEffect(() => {
    async function SetUpStreetData(urlUsrn) {
      if (urlUsrn && urlUsrn !== "-1") {
        if (apiUrl) {
          if (urlUsrn !== dataUsrn.current) {
            dataUsrn.current = urlUsrn;
            setLoading(true);
            loadingRef.current = true;
            if (userContext.currentUser.showMessages)
              console.log("[DEBUG] fetching Street data", dataUsrn.current, `${apiUrl.url}/${urlUsrn}`);
            fetch(`${apiUrl.url}/${urlUsrn}`, {
              headers: apiUrl.headers,
              crossDomain: true,
              method: apiUrl.type,
            })
              .then((res) => (res.ok ? res : Promise.reject(res)))
              .then((res) => {
                switch (res.status) {
                  case 200:
                    return res.json();

                  case 204:
                    streetContext.onStreetErrors(
                      [
                        {
                          field: "USRN",
                          errors: ["This street no longer exists in the database."],
                        },
                      ],
                      [],
                      [],
                      [],
                      [],
                      [],
                      [],
                      [],
                      [],
                      [],
                      [],
                      [],
                      [],
                      [],
                      []
                    );
                    return null;

                  case 401:
                    userContext.onExpired();
                    return null;

                  case 403:
                    streetContext.onStreetErrors(
                      [
                        {
                          field: "USRN",
                          errors: ["You do not have database access."],
                        },
                      ],
                      [],
                      [],
                      [],
                      [],
                      [],
                      [],
                      [],
                      [],
                      [],
                      [],
                      [],
                      [],
                      [],
                      []
                    );
                    return null;

                  case 500:
                    streetContext.onStreetErrors(
                      [
                        {
                          field: "USRN",
                          errors: ["Unexpected server error, please report to support."],
                        },
                      ],
                      [],
                      [],
                      [],
                      [],
                      [],
                      [],
                      [],
                      [],
                      [],
                      [],
                      [],
                      [],
                      [],
                      []
                    );
                    if (userContext.currentUser.showMessages)
                      console.error("[500 ERROR] SetUpStreetData: Unexpected server error.", res);
                    return null;

                  default:
                    streetContext.onStreetErrors(
                      [
                        {
                          field: "USRN",
                          errors: ["Unexpected error, please report to support."],
                        },
                      ],
                      [],
                      [],
                      [],
                      [],
                      [],
                      [],
                      [],
                      [],
                      [],
                      [],
                      [],
                      [],
                      [],
                      []
                    );
                    if (userContext.currentUser.showMessages)
                      console.error("[ERROR] SetUpStreetData: Unexpected error.", res);
                    return null;
                }
              })
              .then(
                (result) => {
                  if (userContext.currentUser.showMessages)
                    console.log("[DEBUG] SetUpStreetData", apiUrl, urlUsrn, JSON.stringify(result));
                  setData(result);
                  if (
                    urlUsrn &&
                    (!streetContext.currentStreet || streetContext.currentStreet.usrn.toString() !== urlUsrn)
                  )
                    streetContext.onUpdateCurrentStreet(
                      Number(urlUsrn),
                      result.streetDescriptors && result.streetDescriptors.length
                        ? result.streetDescriptors.filter((x) => x.language === "ENG")[0].streetDescriptor
                        : "",
                      false
                    );
                  setStore(UpdateSandbox(sandboxStore, "sourceStreet", result, "allStreet"));
                  setStreetExtent(getStreetExtent(result, spatialReferenceWkid.current));
                },
                (error) => {
                  if (userContext.currentUser.showMessages) console.error("[ERROR] Get Street data", error);
                }
              )
              .then(() => {
                setLoading(false);
                loadingRef.current = false;
              });
          }
        }
      } else {
        if (urlUsrn && urlUsrn === "-1" && dataUsrn.current !== "-1" && !loadingRef.current) {
          setLoading(true);
          loadingRef.current = true;
          let newEsus = null;
          if (streetContext.createEsus && Array.isArray(streetContext.createEsus) && streetContext.createEsus.length) {
            newEsus = [];
            GetMultipleEsusData(streetContext.createEsus, userContext).then((result) => {
              result.forEach((esu) => {
                newEsus.push({ ...esu, assignUnassign: 1 });
              });

              streetContext.onCreateStreet(null);

              generateNewStreet(newEsus);
            });
          } else {
            generateNewStreet(newEsus);
          }

          if (urlUsrn && (!streetContext.currentStreet || streetContext.currentStreet.usrn.toString() !== "-1"))
            streetContext.onUpdateCurrentStreet(-1, "Add new Street", true);
        }
      }
    }

    const generateNewStreet = (newEsus) => {
      const newStreet = GetNewStreet(
        settingsContext.isScottish,
        settingsContext.isWelsh,
        settingsContext.authorityCode,
        settingsContext.streetTemplate &&
          settingsContext.streetTemplate.streetTemplate &&
          settingsContext.streetTemplate.streetTemplate.recordType
          ? settingsContext.streetTemplate.streetTemplate.recordType
          : null,
        !settingsContext.isScottish &&
          settingsContext.streetTemplate &&
          settingsContext.streetTemplate.streetTemplate &&
          settingsContext.streetTemplate.streetTemplate.state
          ? settingsContext.streetTemplate.streetTemplate.state
          : null,
        settingsContext.streetTemplate &&
          settingsContext.streetTemplate.streetTemplate &&
          settingsContext.streetTemplate.streetTemplate.localityRef
          ? settingsContext.streetTemplate.streetTemplate.localityRef
          : null,
        settingsContext.streetTemplate &&
          settingsContext.streetTemplate.streetTemplate &&
          settingsContext.streetTemplate.streetTemplate.townRef
          ? settingsContext.streetTemplate.streetTemplate.townRef
          : null,
        settingsContext.isScottish &&
          settingsContext.streetTemplate &&
          settingsContext.streetTemplate.streetTemplate &&
          settingsContext.streetTemplate.streetTemplate.islandRef
          ? settingsContext.streetTemplate.streetTemplate.islandRef
          : null,
        settingsContext.streetTemplate &&
          settingsContext.streetTemplate.streetTemplate &&
          settingsContext.streetTemplate.streetTemplate.adminAreaRef
          ? settingsContext.streetTemplate.streetTemplate.adminAreaRef
          : null,
        !settingsContext.isScottish &&
          settingsContext.streetTemplate &&
          settingsContext.streetTemplate.streetTemplate &&
          settingsContext.streetTemplate.streetTemplate.classification
          ? settingsContext.streetTemplate.streetTemplate.classification
          : null,
        !settingsContext.isScottish &&
          settingsContext.streetTemplate &&
          settingsContext.streetTemplate.streetTemplate &&
          settingsContext.streetTemplate.streetTemplate.streetSurface
          ? settingsContext.streetTemplate.streetTemplate.streetSurface
          : null,
        settingsContext.streetTemplate &&
          settingsContext.streetTemplate.streetTemplate &&
          settingsContext.streetTemplate.streetTemplate.streetExcludeFromExport
          ? settingsContext.streetTemplate.streetTemplate.streetExcludeFromExport
          : false,
        lookupContext,
        userContext.currentUser && userContext.currentUser.hasASD,
        newEsus
      );
      setData(newStreet);
      setStreetExtent(getStreetExtent(newStreet, spatialReferenceWkid.current));
      setStore(UpdateSandbox(sandboxStore, "sourceStreet", newStreet, "allStreet"));
      dataUsrn.current = "-1";
      setLoading(false);
      loadingRef.current = false;
    };

    if (!apiUrl && userContext.currentUser && settingsContext.authorityCode > 0) {
      const streetUrl = GetStreetByUSRNUrl(
        userContext.currentUser,
        !settingsContext.isScottish && userContext.currentUser && userContext.currentUser.hasASD
      );
      setApiUrl(streetUrl);
    }

    if (location.pathname.includes(StreetRoute)) {
      const urlUsrn = location.pathname.replace(`${StreetRoute}/`, "");
      if (urlUsrn !== dataUsrn.current) SetUpStreetData(urlUsrn);
    }

    return () => {};
  }, [
    streetContext,
    userContext,
    settingsContext,
    lookupContext,
    apiUrl,
    location,
    setStore,
    sandboxStore,
    mapContext,
  ]);

  useEffect(() => {
    if (streetContext.streetRestored) {
      dataUsrn.current = -1;
    }
  }, [streetContext.streetRestored]);

  return (
    <EditConfirmationServiceProvider>
      <Box sx={dataFormStyle("ResizePanels")}>
        <PanelGroup autoSaveId="streetPagePanels" direction="horizontal">
          <Panel id="streetDataFormPanel" defaultSize={35} minSize={30} order={1}>
            <StreetDataForm data={data} loading={loading} />
          </Panel>
          <PanelResizeHandle>
            <Box
              sx={{
                width: "5px",
                cursor: "col-resize",
                height: "100%",
                backgroundColor: "transparent",
                "&:hover": {
                  backgroundColor: adsLightGreyD,
                },
              }}
            />
          </PanelResizeHandle>
          <Panel id="streetMapPanel" defaultSize={65} minSize={30} order={2}>
            <ADSMap
              mapType="street"
              startExtent={
                streetExtent
                  ? streetExtent
                  : streetContext.currentStreet?.newStreet && mapContext.currentExtent
                    ? {
                        xmin: mapContext.currentExtent.xmin,
                        ymin: mapContext.currentExtent.ymin,
                        xmax: mapContext.currentExtent.xmax,
                        ymax: mapContext.currentExtent.ymax,
                        spatialReference: mapContext.currentExtent.spatialReference,
                      }
                    : null
              }
            />
          </Panel>
        </PanelGroup>
      </Box>
    </EditConfirmationServiceProvider>
  );
}

export default StreetPage;
